import { CSSProperties } from 'react';

import styled, { css } from 'styled-components';

import { CssUtils } from '@qality/common-utils/src/CssUtils';

import { Cover } from '../Cover';
import { SortableHeader } from './SortableHeader';
import { Widths } from './Table.utils';

/**
 * Helpers
 */

type Margins = {
  mt: CSSProperties['marginTop'];
  mb: CSSProperties['marginBottom'];
  ml: CSSProperties['marginLeft'];
  mr: CSSProperties['marginRight'];
};

const ContentHelper = styled.div<
  Partial<
    {
      muted: boolean;
      height: 'default' | 'compact' | 'full';
      flex: 'flex' | 'inline-flex';
      justifyContent: CSSProperties['justifyContent'];
      alignItems: CSSProperties['alignItems'];
      overflow: CSSProperties['overflow'];
    } & Margins
  >
>`
  ${({ overflow = 'visible' }) => {
    return (
      overflow &&
      css`
        overflow: ${overflow};
      `
    );
  }}

  ${({ mt = 0, mb = 0, ml = 0, mr = 0 }) => {
    return css`
      margin: ${CssUtils.cssUnit(mt)} ${CssUtils.cssUnit(mr)}
        ${CssUtils.cssUnit(mb)} ${CssUtils.cssUnit(ml)};
    `;
  }}

  ${({ muted = false }) => {
    return (
      muted &&
      css`
        opacity: 0.5;
      `
    );
  }}

  ${({ height = 'default' }) => {
    switch (height) {
      case 'default': {
        return css`
          min-height: 32px;
        `;
      }
      case 'compact': {
        return css`
          height: 20px;
        `;
      }
      case 'full': {
        return css`
          height: 100%;
        `;
      }
    }
  }}

  ${({ flex = 'flex' }) => {
    return css`
      display: ${flex};
    `;
  }}

  ${({ justifyContent = 'start' }) => {
    return css`
      justify-content: ${justifyContent};
    `;
  }}

  ${({ alignItems = 'start' }) => {
    return css`
      align-items: ${alignItems};
    `;
  }}
`;

const TableCover = styled(Cover)``;

const TableContainer = styled.div<Partial<{ isScrollable: boolean }>>`
  width: 100%;
  height: 100%;
  overflow: hidden;

  isolation: isolate;
  position: relative;

  ${({ isScrollable = true }) =>
    isScrollable &&
    css`
      scrollbar-gutter: stable;
      overflow-y: auto;
    `}
`;

/**
 * Table body cells
 */

export const TABLE_CELL_HEIGHT_PX = 40;

const TableTd = styled.td<Partial<{ borderTop: boolean; widths: Widths }>>`
  padding: 4px 0px;
  height: ${TABLE_CELL_HEIGHT_PX}px;
  min-height: ${TABLE_CELL_HEIGHT_PX}px;
  word-wrap: break-word;

  ${({ borderTop = false }) => {
    return (
      borderTop &&
      css`
        border-top: 1px solid var(--ds-border, var(--ds-fb-border));
      `
    );
  }}

  ${({ widths }) => {
    return (
      widths &&
      css`
        width: ${widths.width};
        min-width: ${widths.minWidth};
        max-width: ${widths.maxWidth};
      `
    );
  }}
`;

const CheckboxTableTd = styled(TableTd)`
  padding: 4px 0px;
`;

const DndTableTd = styled(TableTd)<
  Partial<{ verticalAlign: CSSProperties['verticalAlign']; isHidden: boolean }>
>`
  cursor: grab;
  padding: 4px 0px;

  ${({ isHidden = true }) => {
    return (
      isHidden &&
      css`
        opacity: 0;
      `
    );
  }};

  ${({ verticalAlign = 'top' }) => {
    return css`
      vertical-align: ${verticalAlign};
    `;
  }};
`;

/**
 * Table header cells
 */

export const TABLE_HEADER_CELL_HEIGHT_PX = 24;
const TableTh = styled.th<{ widths: Widths }>`
  padding: 2px 0px 1px;
  height: ${TABLE_HEADER_CELL_HEIGHT_PX}px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.333;
  letter-spacing: -0.1px;
  color: var(--ds-text-subtlest, var(--ds-fb-text-subtlest));
  border-bottom: 2px solid var(--ds-border, var(--ds-fb-border));

  ${({ widths }) => {
    return css`
      width: ${widths.width};
      min-width: ${widths.minWidth};
      max-width: ${widths.maxWidth};
    `;
  }}
`;

const CheckboxTableTh = styled(TableTh)`
  padding: 0px;
`;

const DndTableTh = styled(TableTh)`
  padding: 0px;
`;

const TableFillerTh = styled(TableTh)`
  padding: 0 !important;
`;

const ClickableTh = styled(TableTh)`
  &:hover {
    background-color: var(--ds-surface-hovered, var(--ds-fb-surface-hovered));
    cursor: pointer;
  }

  &:focus {
    background-color: var(--ds-surface-pressed, var(--ds-fb-surface-pressed));
    outline: none;
  }

  &:active {
    background-color: var(--ds-surface-pressed, var(--ds-fb-surface-pressed));
  }
`;

/**
 * Table rows
 */

const TableTr = styled.tr<
  Partial<{
    isClickable: boolean;
    isHoverable: boolean;
    isHighlighted: boolean;
    isInvalid: boolean;
    isMuted: boolean;
    hideOverflow: boolean;
  }>
>`
  ${({ hideOverflow = false }) => {
    return (
      hideOverflow &&
      css`
        overflow: hidden;
        white-space: nowrap;
      `
    );
  }}

  ${({ isClickable = false }) => {
    return (
      isClickable &&
      css`
        cursor: pointer;
      `
    );
  }}

  ${({ isInvalid = false }) => {
    return (
      isInvalid &&
      css`
        background-color: var(
          --ds-background-accent-red-subtlest,
          var(--ds-fb-background-accent-red-subtlest)
        ) !important;
      `
    );
  }}


  ${({ isHoverable = false }) => {
    return (
      isHoverable &&
      css`
        transition: background 0.15s;

        &:hover {
          background: var(
            --ds-surface-hovered,
            var(--ds-fb-surface-hovered)
          ) !important;
        }
      `
    );
  }}

  ${({ isHighlighted = false }) => {
    return (
      isHighlighted &&
      css`
        background: var(--ds-surface-hovered, var(--ds-fb-surface-hovered));

        & > ${TableTd} {
          border-bottom: 2px solid var(--ds-border, var(--ds-fb-border)) !important;
        }
      `
    );
  }}

  ${({ isMuted = false }) => {
    return (
      isMuted &&
      css`
        background: var(
          --ds-surface-hovered,
          var(--ds-fb-surface-hovered)
        ) !important;
        opacity: 0.3;
      `
    );
  }}

  .show-on-row-hover {
    opacity: 0;
  }

  &:focus-within,
  &:hover {
    .show-on-row-hover {
      transition: opacity 0.15s;
      opacity: 1;
    }
  }
`;

const DndTableTr = styled(TableTr)<{
  isDragging?: boolean;
  isDraggingHighlighted?: boolean;
}>`
  border: none;
  background: var(--ds-surface, var(--ds-fb-surface));

  &:hover ${DndTableTd} {
    opacity: 1;
  }

  ${({ isDragging }) => {
    return (
      isDragging &&
      css`
        box-shadow: var(
          --ds-shadow-raised,
          var(--ds-fb-shadow-raised)
        ) !important;

        background: var(
          --ds-surface-raised,
          var(--ds-fb-surface-raised)
        ) !important;

        ${DndTableTd} {
          opacity: 1;
        }
      `
    );
  }}

  ${({ isDraggingHighlighted }) => {
    return (
      isDraggingHighlighted &&
      css`
        border: solid 2px
          var(
            --ds-background-accent-orange-subtle,
            var(--ds-fb-background-accent-orange-subtle)
          );

        border-radius: 8px;

        box-shadow: var(
          --ds-shadow-overlay,
          var(--ds-fb-shadow-overlay)
        ) !important;

        background: var(
          --ds-background-accent-orange-subtlest,
          var(--ds-fb-background-accent-orange-subtlest)
        ) !important;
      `
    );
  }}
`;

/**
 * Table
 */

const TableHead = styled.thead`
  border-bottom: none;
`;

const TableBody = styled.tbody`
  border-bottom: none;
`;

const TableFooter = styled.tfoot`
  border-bottom: none;
`;

const CELLS_SPACING_PX = 8;

const Table = styled.table<
  Partial<{
    cellsSpacing: number;
    addStickyHeader: boolean;
    addLinesBetweenRows: boolean;
    useFixedLayout: boolean;
    fullWidth: boolean;
    fullHeight: boolean;
    verticalAlign: CSSProperties['verticalAlign'];
    firstColLeftSpacing: 'none' | 'half' | 'full';
    lastColRightSpacing: 'none' | 'half' | 'full';
  }>
>`
  border-collapse: collapse;

  ${({ fullWidth = true }) => {
    return (
      fullWidth &&
      css`
        width: 100%;
      `
    );
  }};

  ${({ fullHeight = false }) => {
    return (
      fullHeight &&
      css`
        height: 100%;
      `
    );
  }};

  ${({ useFixedLayout = false }) => {
    return (
      useFixedLayout &&
      css`
        table-layout: fixed;
      `
    );
  }};

  &&,
  ${TableHead},
    ${TableBody},
    ${TableTr},
    ${TableTh},
    ${TableTd},
    ${ContentHelper} {
    box-sizing: border-box;
  }

  ${TableHead} {
    ${({ addStickyHeader = false }) => {
      return (
        addStickyHeader &&
        css`
          z-index: 1;
          position: sticky;
          top: 0;
          left: 0;
          background: var(--ds-surface, var(--ds-fb-surface));

          // ::after pseudo-element to fix border disappearing on scroll
          &::after {
            content: '';
            display: block;
            border-bottom: 2px solid var(--ds-border, var(--ds-fb-border));
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 0px;
          }
        `
      );
    }}
  }

  ${TableTr} {
    ${({ verticalAlign = 'top' }) => {
      return css`
        vertical-align: ${verticalAlign};
      `;
    }};

    ${({ addLinesBetweenRows = false }) => {
      return (
        addLinesBetweenRows &&
        css`
          border-bottom: 1px solid var(--ds-border, var(--ds-fb-border));

          &:last-of-type {
            border-bottom: none;
          }
        `
      );
    }}
  }

  ${TableTh}:not(${DndTableTh}, ${CheckboxTableTh}),
  ${TableTd}:not(${DndTableTd}, ${CheckboxTableTd}) {
    ${({ cellsSpacing = CELLS_SPACING_PX }) => {
      return css`
        padding-left: ${cellsSpacing}px;
        padding-right: ${cellsSpacing}px;
      `;
    }}

    &:first-of-type {
      ${({ firstColLeftSpacing = 'full', cellsSpacing = CELLS_SPACING_PX }) => {
        if (firstColLeftSpacing === 'none') {
          return css`
            padding-left: 0px;
          `;
        }

        if (firstColLeftSpacing === 'half') {
          return css`
            padding-left: ${cellsSpacing / 2}px;
          `;
        }
      }};
    }

    &:last-of-type {
      ${({ lastColRightSpacing = 'full', cellsSpacing = CELLS_SPACING_PX }) => {
        if (lastColRightSpacing === 'none') {
          return css`
            padding-right: 0px;
          `;
        }

        if (lastColRightSpacing === 'half') {
          return css`
            padding-right: ${cellsSpacing / 2}px;
          `;
        }
      }};
    }
  }
`;

/**
 * Object.assign() is a type-safe method to compose React components.
 * It allows to use components with dot notation, eg. <Table.Th />.
 * To unify Table API this should be only exported component.
 * All the rest components should be 'private' and accessed only
 * via composition.
 */
export default Object.assign(Table, {
  ContentHelper,
  Cover: TableCover,
  Container: TableContainer,

  Head: TableHead,
  Body: TableBody,
  Footer: TableFooter,

  Tr: TableTr,
  DndTr: DndTableTr,

  Th: TableTh,
  FillerTh: TableFillerTh,
  DndTh: DndTableTh,
  CheckboxTh: CheckboxTableTh,
  ClickableTh: ClickableTh,
  SortableTh: SortableHeader,

  Td: TableTd,
  DndTd: DndTableTd,
  CheckboxTd: CheckboxTableTd,
});
